import React, { useState, useEffect, useCallback } from "react";
import CoachMembershipSkeleton from "../../components/layout/CoachMembershipSkeleton";
import { createPortal } from "react-dom";
import api from "../../api/api";
import { useTheme } from "../../hooks/useTheme";
import { useAuth } from "../../hooks/useAuth";
//old import { useNavigate, useSearchParams } from "react-router-dom";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
//old import { Calendar, Crown, Clock, XCircle, FileText, Shield, ScrollText, ArrowUpRight } from "lucide-react";
import { Calendar, Crown, Clock, XCircle, FileText, Shield, ArrowUpRight, CreditCard } from "lucide-react";

// A confirmation stays long enough to read (Jul 20: ~30 seconds)
const NOTICE_MS = 30000;

export default function CoachMembership() {
    const { isDarkMode } = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [membership, setMembership] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cancelling, setCancelling] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [message, setMessage] = useState(null);
    const [undoing, setUndoing] = useState(false);
    // Manage billing: Stripe's page for the card and the receipts
    const [openingBilling, setOpeningBilling] = useState(false);

    useEffect(() => {
        if (!user?.isCoach && !user?.isAdmin) navigate("/userdashboard");
    }, [user, navigate]);

    const fetchMembership = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get("/coach/my-membership");
            setMembership(res.data.membership || null);
        } catch {
            setMembership(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchMembership(); }, [fetchMembership]);

    //old const showMsg = (text, type = "success") => {
    const showMsg = (text, type = "success", ms = 5000) => {
        setMessage({ text, type });
        //old setTimeout(() => setMessage(null), 5000);
        setTimeout(() => setMessage(null), ms);
    };

    // What a pick on the plans page did comes back here to say so (once: a refresh doesn't repeat it)
    useEffect(() => {
        if (!location.state?.notice) return;
        showMsg(location.state.notice, "success", NOTICE_MS);
        navigate(location.pathname, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Returning from Stripe checkout: verify the session, then clean the URL.
    useEffect(() => {
        const sessionId = searchParams.get("session_id");
        if (!sessionId) return;
        (async () => {
            try {
                const res = await api.post("/coach/verify-session", { session_id: sessionId });
                setMembership(res.data.membership || null);
                //old showMsg(res.data.message || "Membership activated.", "success");
                // Evan, Jul 20: Subscription Successful with what was paid ($0.00 for a free trial)
                const m = res.data.membership;
                const paid = typeof res.data.amountPaid === "number" ? ` $${res.data.amountPaid.toFixed(2)} paid today.` : "";
                // (when it's billed, not how much: a promo code's discount can lower it)
                const trial = m?.IsTrialing && m?.EndDate
                    ? ` Free trial until ${new Date(m.EndDate).toLocaleDateString()}, then billed ${m.BillingFrequency === "yearly" ? "yearly" : "monthly"}.`
                    : "";
                showMsg(`Subscription Successful!${paid}${trial}`, "success", NOTICE_MS);
            } catch (err) {
                showMsg(err.response?.data?.error || "Could not verify your payment.", "error");
            } finally {
                setSearchParams({}, { replace: true });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCancelMembership = async () => {
        setCancelling(true);
        try {
            const res = await api.post("/coach/cancel-membership");
            setShowCancelConfirm(false);
            await fetchMembership();
            //old showMsg(res.data.message || "Membership cancelled.", "success");
            // A renewal the card didn't pay ends now instead (Stripe stops retrying it), with no day to keep it until
            showMsg(membership?.StripeSubscriptionStatus === "past_due" && !res.data.endsOn
                ? "Cancelled: you're on the free coach plan now. The payment that didn't go through isn't tried again."
                : res.data.message || "Membership cancelled.", "success");
        } catch (err) {
            showMsg(err.response?.data?.error || "Failed to cancel membership.", "error");
        } finally {
            setCancelling(false);
        }
    };

    // Keep: undo a cancel or a plan picked for the renewal (no charge now)
    const handleKeep = async () => {
        setUndoing(true);
        try {
            const res = await api.post("/coach/cancel-downgrade");
            setMembership(res.data.membership || null);
            showMsg(res.data.message || "You're staying on your current plan.", "success");
        } catch (err) {
            showMsg(err.response?.data?.error || "Could not keep your current plan.", "error");
        } finally {
            setUndoing(false);
        }
    };

    // Manage billing: change the card or see receipts in Stripe's portal (cancelling stays here)
    const handleManageBilling = async () => {
        setOpeningBilling(true);
        try {
            const res = await api.post("/coach/billing");
            window.location.href = res.data.url;
        } catch (err) {
            showMsg(err.response?.data?.error || "Could not open billing.", "error");
            setOpeningBilling(false);
        }
    };

    if (loading) {
        return <CoachMembershipSkeleton isDarkMode={isDarkMode} />;
    }

    // Whether and when the card is charged, as on the student page: a free plan has no
    // date, a plan given at no charge (no card behind it) has no price, renewal or Cancel.
    // Not how much: a promo code's discount stays on the subscription and Stripe charges less
    const paidByCard = membership?.isActive && membership?.HasSubscription;
    const isFree = Number(membership?.Price || 0) === 0 && !membership?.TrialDays;
    const pendingPaid = membership?.PendingTierID && Number(membership?.PendingPrice) > 0;
    // A renewal the card didn't pay (past_due): Stripe tries again, with the new card once it's changed
    const pastDue = paidByCard && membership?.StripeSubscriptionStatus === "past_due";
    const endDate = membership?.EndDate ? new Date(membership.EndDate).toLocaleDateString() : "Ongoing";
    const [dateLabel, dateValue] = isFree ? ["Price", "Free"]
        : !paidByCard || membership?.CancelledAt || (membership?.PendingTierID && !pendingPaid) ? ["Ends", endDate]
        : pendingPaid ? ["Switches", endDate]
        : pastDue ? ["Payment due", endDate]
        : membership?.IsTrialing ? ["First charge", endDate]
        : ["Renews", endDate];
    const statusLine = isFree || !membership?.isActive || membership?.PendingTierID || membership?.CancelledAt ? null
        : !paidByCard ? "Given at no charge: nothing is charged. When it ends you're on the free coach plan."
        : pastDue ? "Your last payment didn't go through. Update your card with Manage billing and we'll try again."
        : membership?.IsTrialing ? `Your free trial ends on ${endDate}. Your first charge is that day.`
        : `Your next charge is on ${endDate}.`;

    return (
        <div className={`max-w-2xl mx-auto space-y-6 py-10 px-4 ${isDarkMode ? "bg-slate-900" : ""}`}>
            <header className="text-center">
                <h1 className={`text-2xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>My Membership</h1>
                <p className={`text-sm mt-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Manage your subscription and billing</p>
            </header>

            {message && (
                <div role="alert" className={`flex items-center gap-3 p-3.5 rounded-xl text-sm font-medium ${
                    message.type === "error"
                        ? isDarkMode ? "bg-red-900/30 text-red-300 border border-red-800/50" : "bg-red-50 text-red-800 border border-red-200"
                        : isDarkMode ? "bg-primary-900/30 text-primary-300 border border-primary-800/50" : "bg-primary-50 text-primary-800 border border-primary-200"
                }`}>{message.text}</div>
            )}

            {/* Cancelled, or another plan picked for when the paid period ends: Keep undoes it (Evan, Sep 9) */}
            {(membership?.PendingTierID || membership?.CancelledAt) && membership?.isActive && (
                <div className={`rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${isDarkMode ? "bg-slate-800 border-warning/40" : "bg-warning-tint border-warning/40"}`}>
                    <div className="flex items-start gap-3 flex-1">
                        <Clock className="text-warning shrink-0 mt-0.5" size={18} />
                        <div>
                            <p className={`text-sm font-semibold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                                {paidByCard && !pendingPaid
                                    ? `Cancelled: ${membership.TierName} ends on ${endDate}`
                                    : `Switching to ${membership.PendingTierName || "the free coach plan"} on ${endDate}`}
                            </p>
                            <p className={`text-xs mt-0.5 ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
                                {pendingPaid
                                    ? `You keep ${membership.TierName} until then. Switch back anytime before that — no charge.`
                                    : paidByCard
                                    ? `Then you're on ${membership.PendingTierName || "the free coach plan"} and your card isn't charged again. Keep ${membership.TierName} anytime before that.`
                                    : `You keep your ${membership.TierName} features until then. Switch back anytime before that — no charge.`}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleKeep}
                        disabled={undoing}
                        className="whitespace-nowrap px-4 py-2 rounded-xl text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 transition-colors disabled:opacity-50"
                    >
                        {undoing ? "Working…" : `Keep ${membership.TierName}`}
                    </button>
                </div>
            )}

            {/* Plan Card */}
            <div className={`rounded-2xl border overflow-hidden ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200 shadow-sm"}`}>
                <div className="bg-slate-900 px-6 py-6 text-center relative">
                    <Crown className="text-amber-400 mx-auto mb-2" size={28} />
                    <h2 className="text-xl font-bold text-white">{membership?.TierName || "Free coach account"}</h2>
                    <span className={`inline-block mt-2 px-3 py-0.5 rounded-full text-xs font-bold border ${
                        membership?.CancelledAt
                            ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
                            : (membership?.isActive || !membership)
                                ? "bg-primary-500/20 text-primary-400 border-primary-500/30"
                                : "bg-red-500/20 text-red-400 border-red-500/30"
                    }`}>
                        {/* old {membership?.CancelledAt ? "Cancelled" : (membership?.isActive || !membership) ? "Active" : "Inactive"} */}
                        {membership?.CancelledAt ? "Cancelled" : membership?.IsTrialing ? "Free trial" : (membership?.isActive || !membership) ? "Active" : "Inactive"}
                    </span>
                    {/* old {membership?.Price ? ( */}
                    {/* Only a plan the card pays for has a price here (one given at no charge has none) */}
                    {Number(membership?.Price) > 0 && membership?.HasSubscription ? (
                        <p className="mt-3">
                            <span className="text-3xl font-extrabold text-primary-400">${Number(membership.Price).toFixed(2)}</span>
                            {/* <span className="text-sm text-slate-400">/mo</span> */}
                            <span className="text-sm text-slate-400">{membership.BillingFrequency === 'yearly' ? '/yr' : '/mo'}</span>
                        </p>
                    ) : null}
                </div>

                <div className="px-6 py-5 space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                        <div className={`rounded-xl p-3 ${isDarkMode ? "bg-slate-700/50" : "bg-slate-50"}`}>
                            <div className="flex items-center gap-2 mb-1">
                                <Calendar className="text-primary-500" size={14} />
                                <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDarkMode ? "text-slate-400" : "text-slate-400"}`}>Started</p>
                            </div>
                            <p className={`text-sm font-bold ${isDarkMode ? "text-slate-200" : "text-slate-900"}`}>
                                {membership?.StartDate ? new Date(membership.StartDate).toLocaleDateString() : "N/A"}
                            </p>
                        </div>
                        <div className={`rounded-xl p-3 ${isDarkMode ? "bg-slate-700/50" : "bg-slate-50"}`}>
                            <div className="flex items-center gap-2 mb-1">
                                <Clock className="text-primary-500" size={14} />
                                {/* old <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDarkMode ? "text-slate-400" : "text-slate-400"}`}>{membership?.CancelledAt ? "Expires" : "Renews"}</p> */}
                                <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDarkMode ? "text-slate-400" : "text-slate-400"}`}>{dateLabel}</p>
                            </div>
                            <p className={`text-sm font-bold ${isDarkMode ? "text-slate-200" : "text-slate-900"}`}>
                                {/* old {membership?.EndDate ? new Date(membership.EndDate).toLocaleDateString() : "Ongoing"} */}
                                {dateValue}
                            </p>
                        </div>
                    </div>
                    {/* (a payment that didn't go through reads like the page's other errors) */}
                    {statusLine && <p className={pastDue ? "text-sm font-medium text-red-500" : `text-sm ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>{statusLine}</p>}

                    <div className="space-y-2">
                        <button
                            className="w-full py-3 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 bg-primary-500 text-white hover:bg-primary-600 shadow-sm"
                            onClick={() => navigate("/coaching/membership/plans")}
                        >
                            <ArrowUpRight size={16} /> {membership?.isActive ? "View Plans" : "Upgrade Now"}
                        </button>
                        {/* The card behind a plan bought with Stripe: change it, see receipts */}
                        {paidByCard && (
                            <button
                                onClick={handleManageBilling}
                                disabled={openingBilling}
                                className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 border disabled:opacity-50 ${
                                    isDarkMode ? "text-slate-200 border-slate-600 hover:bg-slate-700" : "text-slate-700 border-slate-200 hover:bg-slate-50"
                                }`}
                            >
                                <CreditCard size={16} /> {openingBilling ? "Opening…" : "Manage billing"}
                            </button>
                        )}
                        {/* old {membership?.isActive && !membership?.CancelledAt && Number(membership?.Price) > 0 && ( */}
                        {/* Only a plan the card pays for (one given at no charge just ends); a cheaper paid plan waiting can still be cancelled */}
                        {paidByCard && !membership?.CancelledAt && (!membership?.PendingTierID || pendingPaid) && Number(membership?.Price) > 0 && (
                            <button
                                onClick={() => setShowCancelConfirm(true)}
                                className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 border ${
                                    isDarkMode ? "text-red-400 border-red-800/50 hover:bg-red-900/20" : "text-red-500 border-red-200 hover:bg-red-50"
                                }`}
                            >
                                <XCircle size={16} /> Cancel Membership
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className={`flex flex-wrap justify-center gap-5 text-xs ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                {/* The app has no pages of its own for these, so they open the official site like the landing page's links; the site has no separate membership agreement, so it opens the Terms of Service */}
                {/* old <a href="/membership-agreement" className="flex items-center gap-1 hover:text-slate-600 transition-colors"><ScrollText size={12} /> Membership Agreement</a> */}
                {/* Terms of Service and Privacy Policy only, until there's a membership agreement of its own (it opened the Terms of Service) */}
                {/* old <a href="https://buildmybizbrand.com/terms-of-service" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-slate-600 transition-colors"><ScrollText size={12} /> Membership Agreement</a> */}
                {/* old <a href="/terms-of-service" className="flex items-center gap-1 hover:text-slate-600 transition-colors"><FileText size={12} /> Terms of Service</a> */}
                <a href="https://ponopilot.com/terms-of-service" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-slate-600 transition-colors"><FileText size={12} /> Terms of Service</a>
                {/* old <a href="/privacy-policy" className="flex items-center gap-1 hover:text-slate-600 transition-colors"><Shield size={12} /> Privacy Policy</a> */}
                <a href="https://ponopilot.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-slate-600 transition-colors"><Shield size={12} /> Privacy Policy</a>
            </div>

            {showCancelConfirm && createPortal(
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm" style={{ zIndex: 9999 }} onClick={() => setShowCancelConfirm(false)}>
                    <div className={`rounded-2xl shadow-xl max-w-md w-full mx-4 p-6 ${isDarkMode ? "bg-slate-800" : "bg-white"}`} onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-red-100 rounded-full"><XCircle className="text-red-500" size={24} /></div>
                            <h3 className={`text-lg font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Cancel Membership</h3>
                        </div>
                        <p className={`text-sm mb-6 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                            {/* old Are you sure you want to cancel your membership? You'll lose access to premium coaching features. */}
                            {/* The day it ends and that nothing more is charged (Evan, Sep 9); a renewal the card didn't pay ends now, as Stripe then stops retrying it */}
                            {pastDue
                                ? `Your last payment didn't go through, so if you cancel, ${membership?.TierName} ends now and you're on the free coach plan. That payment isn't tried again and nothing more is charged.`
                                : `You keep ${membership?.TierName} until ${endDate}. Then you're on the free coach plan and your card isn't charged again. You can keep ${membership?.TierName} anytime before then.`}
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowCancelConfirm(false)}
                                className={`flex-1 py-3 rounded-xl font-semibold border transition-colors ${isDarkMode ? "border-slate-600 text-slate-300 hover:bg-slate-700" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}
                            >
                                Keep Membership
                            </button>
                            <button
                                onClick={handleCancelMembership}
                                disabled={cancelling}
                                className="flex-1 py-3 rounded-xl font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
                            >
                                {cancelling ? "Cancelling..." : "Yes, Cancel"}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
